import { useMutation, useQueryClient } from 'react-query';
import { apiService } from '~services/ApiService';
import { Notifier, NotifierComponents } from 'react-native-notifier';

// Types for response from the backend
export interface AttendanceStatusResponse {
  success: boolean;
  status: 'on_time' | 'late' | 'not_coming' | null;
  message?: string;
}

// Hook for managing unified attendance status
export const useSetAttendanceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<AttendanceStatusResponse, Error, { order_id: string; status_type: 'on_time' | 'late' | 'not_coming' }>(
    async ({ order_id, status_type }) => {
      // Set specific status
      return await apiService.post<AttendanceStatusResponse>(`orders/${order_id}/attendance-status?status_type=${status_type}`);
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate relevant queries
        queryClient.invalidateQueries(['orderById', variables.order_id]);
        queryClient.invalidateQueries(['attendanceStatus', variables.order_id]);
        
        // Show appropriate notification based on status
        switch (variables.status_type) {
          case 'on_time':
            Notifier.showNotification({
              title: "Great! Have a wonderful evening",
              Component: NotifierComponents.Alert,
              duration: 5000,
              componentProps: {
                alertType: 'success',
              },
            });
            break;
          case 'late':
            Notifier.showNotification({
              title: "We're sorry to hear that — we'll let the other guests know",
              Component: NotifierComponents.Alert,
              duration: 5000,
              componentProps: {
                alertType: 'warn',
              },
            });
            break;
          case 'not_coming':
            Notifier.showNotification({
              title: "Oh. That's not good. But we hope you're OK. We will inform the other guests you are no longer coming",
              Component: NotifierComponents.Alert,
              duration: 5000,
              componentProps: {
                alertType: 'error',
              },
            });
            break;
        }
      },
      onError: (error) => {
        console.error('Error updating attendance status:', error);
        Notifier.showNotification({
          title: 'Failed to update your status',
          description: error.message,
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'error',
          },
        });
      },
    },
  );
};